import { ActorsManager } from "./utils/ActorsManager";
import { SceneCamerasController } from "./utils/SceneCamerasController";
import { Cooker } from "./utils/Cooker";
import { SceneCookController } from "./utils/CookController";
import { CoreGraph } from "../../core/graph/CoreGraph";
import { CorePerformance } from "../../core/performance/CorePerformance";
import { DispatchController } from "./utils/DispatchController";
import { ExpressionsController } from "./utils/ExpressionsController";
import { SceneLifeCycleController } from "./utils/LifeCycleController";
import { LoadingController } from "./utils/LoadingController";
import { MissingReferencesController } from "./utils/missingReferences/MissingReferencesController";
import { NodesController } from "./utils/NodesController";
import { PolySceneSerializer } from "./utils/Serializer";
import { SceneEventsDispatcher } from "./utils/events/EventsDispatcher";
import { ObjectsController } from "./utils/ObjectsController";
import { ReferencesController } from "./utils/ReferencesController";
import { TimeController } from "./utils/TimeController";
import { UniformsController } from "./utils/UniformsController";
import { ViewersRegister } from "./utils/ViewersRegister";
import { SceneWebGLController } from "./utils/WebGLController";
import { WindowController } from "./utils/WindowController";
import { SceneAssetsController } from "./utils/AssetsController";
import { CoreString } from "../../core/String";
import { SceneRenderersRegister } from "./utils/SceneRenderersRegister";
import { Poly } from "../Poly";
export class PolyScene {
  constructor() {
    this._cooker = new Cooker(this);
    this.cookController = new SceneCookController();
    this._graph = new CoreGraph();
    this._missing_expression_references_controller = new MissingReferencesController(this);
    this._expressions_controller = new ExpressionsController();
    this._nodes_controller = new NodesController(this);
    this.renderersRegister = new SceneRenderersRegister(this);
    this._objects_controller = new ObjectsController(this);
    this._references_controller = new ReferencesController(this);
    this._time_controller = new TimeController(this);
    this._readOnly = false;
    this._graph.setScene(this);
    this.nodesController.init();
    Poly.scenesRegister.registerScene(this);
  }
  threejsScene() {
    return this.root().object;
  }
  setUuid(uuid) {
    return this._uuid = uuid;
  }
  get uuid() {
    return this._uuid;
  }
  setName(newName) {
    newName = CoreString.sanitizeName(newName);
    return this._name = newName;
  }
  name() {
    return this._name;
  }
  get camerasController() {
    return this._camerasController = this._camerasController || new SceneCamerasController(this);
  }
  mainCamera() {
    return this.camerasController.mainCamera();
  }
  get cooker() {
    return this._cooker;
  }
  get actorsManager() {
    return this._actorsManager = this._actorsManager || new ActorsManager(this);
  }
  get assets() {
    return this._assets_controller = this._assets_controller || new SceneAssetsController();
  }
  async waitForCooksCompleted() {
    return await this.cookController.waitForCooksCompleted();
  }
  get dispatchController() {
    return this._dispatch_controller = this._dispatch_controller || new DispatchController(this);
  }
  get eventsDispatcher() {
    return this._events_dispatcher = this._events_dispatcher || new SceneEventsDispatcher(this);
  }
  get graph() {
    return this._graph;
  }
  get lifecycleController() {
    return this._lifecycleController = this._lifecycleController || new SceneLifeCycleController(this);
  }
  get loadingController() {
    return this._loading_controller = this._loading_controller || new LoadingController(this);
  }
  get missingExpressionReferencesController() {
    return this._missing_expression_references_controller;
  }
  get expressionsController() {
    return this._expressions_controller;
  }
  get nodesController() {
    return this._nodes_controller;
  }
  createNode(nodeClass, paramsInitValueOverrides) {
    return this.root().createNode(nodeClass, paramsInitValueOverrides);
  }
  nodesByType(type) {
    return this.nodesController.nodesByType(type);
  }
  get objectsController() {
    return this._objects_controller;
  }
  findObjectByMask(mask) {
    return this._objects_controller.findObjectByMask(mask);
  }
  objectsByMask(mask) {
    return this._objects_controller.objectsByMask(mask);
  }
  get referencesController() {
    return this._references_controller;
  }
  get performance() {
    return this._performance = this._performance || new CorePerformance();
  }
  get viewersRegister() {
    return this._viewers_register = this._viewers_register || new ViewersRegister(this);
  }
  get timeController() {
    return this._time_controller;
  }
  setFrame(frame) {
    this.timeController.setFrame(frame);
  }
  setFrameToStart() {
    this.timeController.setFrameToStart();
  }
  frame() {
    return this.timeController.frame();
  }
  time() {
    return this.timeController.time();
  }
  maxFrame() {
    return this.timeController.maxFrame();
  }
  play() {
    this.timeController.play();
  }
  pause() {
    this.timeController.pause();
  }
  incrementTime(options) {
    this.timeController.incrementTime(options);
  }
  incrementTimeIfPlaying(options) {
    this.timeController.incrementTimeIfPlaying(options);
  }
  registerRenderer(renderer, options) {
    return this.renderersRegister.registerRenderer(renderer, options);
  }
  get serializer() {
    return this._serializer = this._serializer || new PolySceneSerializer(this);
  }
  toJSON() {
    return this.serializer.toJSON();
  }
  markAsReadOnly(requester) {
    if (this._readOnly) {
      return;
    }
    this._readOnlyRequester = requester;
    this._readOnly = true;
  }
  readOnly() {
    return this._readOnly;
  }
  readOnlyRequester() {
    return this._readOnlyRequester;
  }
  get uniformsController() {
    return this._uniformsController = this._uniformsController || new UniformsController(this);
  }
  get webgl_controller() {
    return this._webgl_controller = this._webgl_controller || new SceneWebGLController();
  }
  get windowController() {
    return this._windowController = this._windowController || new WindowController(this);
  }
  dispose() {
    var _a;
    (_a = this._windowController) == null ? void 0 : _a.dispose();
    Poly.scenesRegister.deregisterScene(this);
  }
  async batchUpdates(callback) {
    this._cooker.block();
    await callback();
    this._cooker.unblock();
  }
  node(path) {
    return this.nodesController.node(path);
  }
  root() {
    return this.nodesController.root();
  }
  registerOnBeforeTick(callbackName, callback) {
    this.timeController.registerOnBeforeTick(callbackName, callback);
  }
  unRegisterOnBeforeTick(callbackName) {
    this.timeController.unRegisterOnBeforeTick(callbackName);
  }
  registeredBeforeTickCallbacks() {
    return this.timeController.registeredBeforeTickCallbacks();
  }
  registerOnAfterTick(callbackName, callback) {
    this.timeController.registerOnAfterTick(callbackName, callback);
  }
  unRegisterOnAfterTick(callbackName) {
    this.timeController.unRegisterOnAfterTick(callbackName);
  }
  registeredAfterTickCallbacks() {
    return this.timeController.registeredAfterTickCallbacks();
  }
}
